/**
 * Single source of truth for how a CPM campaign card / detail page is gated
 * for the current user. Precedence: "selected" mode (see below) wins outright,
 * then the pre-existing partner-campaign PRO gate, then joined, then invite
 * lock, then the free-plan 1-campaign limit reported by the backend
 * (`join_blocked_reason`).
 *
 * Invite-only campaigns unlock two ways: an approved channel request
 * (`approvedPlatformCount > 0`, the pre-existing manual-selection path) OR
 * having any paid plan (`isPaidUser` — Starter/Plus/Pro+, not just Pro+;
 * widened from Pro+-only on 03/08/2026 per CEO request) — either bypasses
 * the lock, neither is required if the other is present. Mirrors the
 * backend's `joinCpmCampaign` gate (`hasPrivateCampaignsAccess` in
 * `cpmParticipation.js`, autoclipper-backend) so a user who sees this as
 * joinable can actually join — **that backend mirror still checks Pro+ only
 * as of this comment**, so this frontend change alone would show
 * Starter/Plus viewers an unlocked card whose join click still fails. Needs
 * the backend counterpart before shipping for real.
 *
 * "Selected" mode (`isSelectedMode`) is a separate, fully exclusive campaign
 * type — not a third way to unlock invite-only. Only viewers on the
 * campaign's hand-picked list can join; paid plan and approved channel don't
 * apply. Use case: a brand runs an open campaign, sees who performed best in
 * the audit, then launches a follow-up campaign restricted to exactly those
 * clippers. **No backend field exists yet for this** (no `access_mode` /
 * selected-users relation on `cpm-campaign`) — this state exists so the
 * frontend structure is ready once one does.
 */
export type CampaignAccessState =
  | 'joined'
  | 'joinable'
  | 'free_limit_locked'
  | 'invite_locked'
  | 'pro_locked'
  | 'selected_locked';

export function deriveCampaignAccess({
  isJoined,
  joinBlockedReason,
  inviteOnly,
  approvedPlatformCount,
  isPartnerCampaign,
  isProUser,
  isPaidUser,
  isSelectedMode = false,
  isSelectedUser = false,
}: {
  isJoined: boolean;
  joinBlockedReason: string | null | undefined;
  inviteOnly: boolean;
  approvedPlatformCount: number;
  isPartnerCampaign: boolean;
  isProUser: boolean;
  isPaidUser: boolean;
  isSelectedMode?: boolean;
  isSelectedUser?: boolean;
}): CampaignAccessState {
  if (isSelectedMode) {
    if (isJoined) return 'joined';
    return isSelectedUser ? 'joinable' : 'selected_locked';
  }
  if (isPartnerCampaign && !isProUser) return 'pro_locked';
  if (isJoined) return 'joined';
  if (inviteOnly && approvedPlatformCount === 0 && !isPaidUser) return 'invite_locked';
  if (joinBlockedReason === 'free_campaign_limit') return 'free_limit_locked';
  return 'joinable';
}
